import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { MultiSelectOption, NestedKeyOf } from './CommonTypes';
import { AttributeOptions } from './company';
import { ResultStatus } from './QueryResult';
import { Ability } from '@casl/ability';
import { Actions, Subjects } from './permission';

export enum Cat {
  NONE = 'none',
  PREFERRED = 'preferred',
  REQUIRED = 'required',
}

export enum PriorityLevel {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export type Attribute = {
  attribute?: string;
  name?: string;
  value?: string;
  attributeValue?: string;
  cat: Cat | string;
  isPrepopulated: boolean;
  minimumProficiency: number;
  nonCat: Cat | string;
  useProficiency: boolean;
};

export type RolePriorities = {
  id: string;
  order: number;
};

export enum Duration {
  S = 's',
  M = 'm',
  L = 'l',
}

export type TaskDuration = {
  duration: Duration;
  peril: string;
};

export interface MetaData {
  created?: string | null;
  deleted?: string | null;
  edited: string | Date | null;
  isActive?: boolean;
  timesUsed?: number;
}

export interface ParsedRule {
  description?: string;
  ruleDescription?: string;
  targetTaskGroupId: string;
  condition?: string;
  rules: Rule[];
  ifThis?: string;
  operator?: string;
  meta?: MetaData[];
}
export interface RoutingRule {
  rules: Rule[];
}
export interface TaskTemplateForm {
  id?: string;
  tenantId?: string;
  attributes?: Attribute[];
  description?: string;
  externalId: string;
  isStateLicenseRequired: boolean;
  isTravelRequired: boolean;
  name: string;
  priorityLevel?: PriorityLevel;
  rolePriorities?: RolePriorities[];
  ruleDescription?: string;
  condition?: string;
  targetTaskGroupId: string;
  rules: FormRule[];
  sloInMinutes?: string;
  durations?: TaskDuration[];
  createdAt: string | null;
  editedAt: string | null;
  deletedAt: string | null;
  deactivatedAt: string | null;
  timesUsed: number;
}

export interface FormRule {
  ifThis: string;
  operator: string;
  meta: string[] | string;
}

export interface TaskTemplate {
  id: string;
  tenantId: string;
  externalId: string;
  name: string;
  description: string;
  metaData: MetaData;
  rolePriorities: RolePriorities[];
  durations?: TaskDuration[];
  sloInMinutes: number;
  priorityLevel: PriorityLevel | null;
  isTravelRequired: boolean;
  isStateLicenseRequired: boolean;
  attributes: Attribute[] | null;
  templateProperties: Partial<
    Record<keyOfTemplateProperties, ValueOfTemplateProperty>
  >;
  rule: Rule;
  createdAt: string | null;
  editedAt: string | null;
  deletedAt: string | null;
  deactivatedAt: string | null;
  timesUsed: number;
}

type ValueOfTemplateProperty = string | number | boolean | Attribute[];

export interface TaskTemplatesPostBody {
  name: string;
  externalId: string;
  createdAt: string | null;
  editedAt: string | null;
  deletedAt: string | null;
  deactivatedAt: string | null;
  timesUsed: number;
  description: string | undefined;
  rolePriorities: RolePriorities[] | null;
  durations?: TaskDuration[];
  sloInMinutes: string;
  priorityLevel: PriorityLevel | null;
  isTravelRequired: boolean;
  isStateLicenseRequired: boolean;
  templateProperties: Record<string, unknown>;
  rule: Rule;
}

export interface TaskFormComponentProps {
  control: Control<FieldValues>;
  errors: FieldErrors;
  rulesState?: RulesState[];
  name: string;
  setValue?: UseFormSetValue<TaskTemplateForm & FieldValues>;
  getValues?: UseFormGetValues<TaskTemplateForm>;
  attributeDefinitions?: AttributeOptions[];
  options?: MultiSelectOption[];
}

export interface RulesState {
  ifThis: string;
  operator: string;
  meta: string | string[];
}

export interface TaskTemplateDetailProps {
  data?: TaskTemplateForm;
  onSubmit: (values: TaskTemplateForm) => void;
  onDeactivate?: (values: TaskTemplateForm) => void;
  onActivate?: (values: TaskTemplateForm) => void;
  status: ResultStatus;
  attributeDefinitions?: AttributeOptions[];
  companyUserRoleOptions?: MultiSelectOption[];
  perilOptions?: MultiSelectOption[];
  taskGroupOptions?: MultiSelectOption[];
  permissions: Ability<[Actions, Subjects]> | undefined;
}

export interface TaskPeril {
  code: string;
  description: string;
  group: string;
  color?: string;
  iconClassName?: string;
}

// new type for conditions
type TaskMessage = {
  $msg: {
    claim: {
      peril: string;
      lossLocation: {
        state: string;
      };
    };
    insured: {
      name: string;
      cell: string;
    };
  };
};

type TemplateProperties = {
  $msg: {
    assignmentHandling: {
      durationInMinutes: number;
      priority?: PriorityLevel;
      travel: string;
    };
    attributes?: Attribute[];
  };
};

type keyOfTemplateProperties = NestedKeyOf<TemplateProperties>;

type KeyOfTaskMessage = NestedKeyOf<TaskMessage>;

type InListExpression = {
  '@in_list': [KeyOfTaskMessage, string[]];
};

type NotInListExpression = {
  '@not': [InListExpression];
};

export type TaskTemplateRuleDropDown = {
  label: string;
  value: KeyOfTaskMessage;
};

type Expression = InListExpression | NotInListExpression;

// boolean expressions
export type OrExpression = {
  '@or': [Expression, Expression?];
};

export type AndExpression = {
  '@and': [Expression, Expression];
};

export interface Rule {
  description?: string;
  ruleDescription?: string;
  targetTaskGroupId: string;
  ifThis?: string;
  operator?: string;
  meta?: MetaData[];
  conditions: Partial<Condition>[];
}

export type Condition = AndExpression | OrExpression;
